import { cn } from '@/lib/utils';
import type { TaskLabelView } from '@/lib/api/generated/model';

import { LABEL_CHIP_SHAPE, type LabelChipDensity } from '../chipShape';
import { labelSlotClass } from '../labelSlots';

export interface LabelChipProps {
  /**
   * Only what a chip actually draws.
   *
   * Deliberately narrower than `TaskLabelView`: a chip renders a name in a slot
   * and reads nothing else. Taking the whole view forced every caller holding a
   * `LabelView` — the settings list, the picker, the filter — to fabricate the
   * missing fields, and the one they invented was `actor_type: 'user'`, which
   * is a claim about *who applied the label* that none of those surfaces is in
   * a position to make.
   */
  label: Pick<TaskLabelView, 'name' | 'color'>;
  density?: LabelChipDensity;
  className?: string;
}

/**
 * One label, rendered the way a label is always rendered: a filled pill
 * containing its name.
 *
 * **Never a dot and never a rail.** Those two forms are how the board says
 * *state* — the coloured rail down a card, the leading dot on a header pill —
 * and a label that borrowed either would be read as a state by anyone scanning.
 * The form is what keeps the two languages apart, and it is what still keeps
 * them apart now that the eight slots carry eight tints (waiver WD-01): a chip
 * is coloured, but it is coloured in a shape state never takes.
 *
 * Inert by construction: no `tabIndex`, no handlers, no nested control. A card
 * is already a drag handle and a click-to-open-drawer target, so a chip that
 * captured either gesture would make the card misfire. Where a chip *should*
 * act — in the drawer, on a search row, in a filter — the caller reaches for
 * `RemovableLabelChip` instead.
 */
export function LabelChip({
  label,
  density = 'card',
  className,
}: LabelChipProps) {
  return (
    <span
      data-testid="label-chip"
      className={cn(
        LABEL_CHIP_SHAPE[density],
        'px-2.5',
        labelSlotClass(label.color),
        className,
      )}
    >
      {/*
        Its own element, because an ellipsis needs a block to end in: text
        sitting directly in an inline-flex pill is an anonymous flex item that
        can be clipped but never truncated. Only the Tasks page lets a chip
        shrink; at full width the span changes nothing, measured in Chromium on
        a card chip: the same pill box, and the text at the same pixels.
      */}
      <span className="min-w-0 truncate">{label.name}</span>
    </span>
  );
}
